// src/CartContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { Product, CartItem, CartModifier } from './types';

// Расширяем CartItem, так как внутри контекста мы добавляем уникальный cartId
interface ExtendedCartItem extends CartItem {
    cartId?: string; // Уникальный ID строки в корзине (для модификаторов)
}

interface CartContextType {
    cart: ExtendedCartItem[];
    addToCart: (product: Product, modifiers?: CartModifier[], qty?: number) => void;
    removeFromCart: (cartId: number | string) => void;
    decreaseQuantity: (cartId: number | string) => void;
    clearCart: () => void;
    getCartTotal: () => number;
    cartItems: ExtendedCartItem[];
    cartTotal: number;
    totalItems: number;
}

export const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

interface CartProviderProps {
    children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<ExtendedCartItem[]>(() => {
    try {
      const saved = localStorage.getItem('moyoo_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('moyoo_cart', JSON.stringify(cart));
  }, [cart]);

  // Сравнение модификаторов (для группировки в корзине)
  const areModifiersEqual = (mods1: CartModifier[] = [], mods2: CartModifier[] = []) => {
    if (mods1.length !== mods2.length) return false;
    const s1 = [...mods1].sort((a, b) => a.name.localeCompare(b.name));
    const s2 = [...mods2].sort((a, b) => a.name.localeCompare(b.name));
    return JSON.stringify(s1) === JSON.stringify(s2);
  };

  const addToCart = (product: Product, modifiers: CartModifier[] = [], qty: number = 1) => {
    setCart((prev) => {
      // Ищем товар с такими же ID и модификаторами
      const existingIndex = prev.findIndex(item => 
        item.id === product.id && areModifiersEqual(item.selectedModifiers, modifiers)
      );

      if (existingIndex !== -1) {
        const newCart = [...prev];
        newCart[existingIndex].quantity += qty;
        return newCart;
      } else {
        // Создаем новый элемент корзины
        // line_total считается сразу, чтобы не пересчитывать каждый раз
        const modsPrice = modifiers.reduce((acc, m) => acc + m.price, 0);
        const newItem: ExtendedCartItem = {
            ...product,
            quantity: qty,
            selectedModifiers: modifiers,
            line_total: (product.price + modsPrice) * qty,
            // Генерируем уникальный ID для этой позиции, чтобы можно было удалить именно её
            cartId: `${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };
        return [...prev, newItem];
      }
    });
  };

  const removeFromCart = (cartId: number | string) => {
    // Если передан ID товара (старая логика), удаляем все вариации
    // Если cartId (новая логика), удаляем конкретную
    setCart(prev => prev.filter(item => {
        if (item.cartId) {
            return item.cartId !== cartId;
        }
        return item.id !== cartId;
    }));
  };

  const decreaseQuantity = (cartId: number | string) => {
    setCart(prev => {
        return prev.reduce((acc: ExtendedCartItem[], item) => {
            // Поддержка старой (по id) и новой (по cartId) логики
            const match = item.cartId ? item.cartId === cartId : item.id === cartId;
            
            if (match) {
                if (item.quantity > 1) {
                    acc.push({ ...item, quantity: item.quantity - 1 });
                }
                // Если 1, то не пушим (удаляем)
            } else {
                acc.push(item);
            }
            return acc;
        }, []);
    });
  };

  const clearCart = () => setCart([]);

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
        const modsPrice = item.selectedModifiers?.reduce((sum, m) => sum + (m.price || 0), 0) || 0;
        return total + (item.price + modsPrice) * item.quantity;
    }, 0);
  };

  // АЛИАСЫ ДЛЯ СОВМЕСТИМОСТИ
  const cartItems = cart;
  const cartTotal = getCartTotal();
  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <CartContext.Provider value={{ 
        cart, 
        addToCart, 
        removeFromCart, 
        decreaseQuantity, 
        clearCart, 
        getCartTotal,
        cartItems,
        cartTotal,
        totalItems
    }}>
      {children}
    </CartContext.Provider>
  );
};
